import React, { useRef, useState } from 'react'
import {
  useFloating,
  useHover,
  useFocus,
  useInteractions,
  useDismiss,
  useRole,
  FloatingPortal,
  FloatingArrow,
  arrow,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react'
import { Z_INDEX } from 'styles/z-index'

const SAGE_LT = '#D6E2DA'

interface NavTooltipProps {
  children: React.ReactElement
  label: string
}

export const NavTooltip: React.FC<NavTooltipProps> = ({ children, label }) => {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'right',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  })

  const hover = useHover(context, {
    move: false,
    delay: { open: 100, close: 0 },
  })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ])

  // Clone the child and attach the floating-ui ref + interaction props directly
  // to the button element so it has a real bounding box for positioning.
  const trigger = React.cloneElement(children as React.ReactElement<any>, {
    ref: refs.setReference as any,
    ...getReferenceProps(),
  })

  return (
    <>
      {trigger}
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: Z_INDEX.TOOLTIP,
              backgroundColor: SAGE_LT,
              boxShadow: '0px 4px 8px 3px rgba(6, 81, 47, 0.1)',
            }}
            className="flex items-center px-2 py-1 rounded text-fg text-xs font-medium whitespace-nowrap"
            {...getFloatingProps()}
          >
            {label}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill={SAGE_LT}
              width={10}
              height={5}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
